import React, { useEffect, useState, useMemo, useLayoutEffect } from 'react';
import { CardBrand, FilterType, SearchFilter } from '../DashboardClasses';
import { Button, Header, Icon, Input, Label, Modal, Placeholder, Table } from 'react-frontier';
import { useUser } from '../Hooks';
import { addCommas, bindClose, getCardBrandIcon } from '@payfront/components/Util';
import { useTranslation } from 'react-i18next';
import { Link, NavigationType, useNavigationType } from 'react-router-dom';
import API from '../API';
import moment from 'moment';
import Dropdown from '../components/Dropdown';

interface Payment{
	payment_id: number,
	client_id?: number,
	client_name?: string,
	card_name: string,
	card_digits: number,
	card_brand: CardBrand,
	paid: boolean,
	cancelled: boolean,
	total: number,
	date_paid: number,
	date_cancelled: number,
	date_expiration: number,
	date_created: number,
}

type SearchFilterType = {
	key: 'buyer_email' | 'external_id' | 'total' | 'reference' | 'card_digits' | 'email',
	type: FilterType, 
	name: string,
	short_name?: string,
}
const SEARCH_FILTERS : SearchFilterType[] = [
	{ key: 'buyer_email', type: FilterType.LIKE, name: 'Correo electrónico', short_name: 'Email' }, 
	{ key: 'external_id', type: FilterType.LIKE, name: 'Folio negocio', short_name: 'Folio' }, 
	{ key: 'total', type: FilterType.VALUE, name: 'Monto' }, 
	{ key: 'reference', type: FilterType.VALUE, name: 'Referencia' }, 
	{ key: 'card_digits', type: FilterType.VALUE, name: 'Terminación de tarjeta', short_name: 'Tarjeta' },
]

var PaymentStatusLabel = (props: { payment: Payment, style?: React.CSSProperties })=>{
	var { t } = useTranslation();
	var now = useMemo(()=>{
		return moment().unix();
	}, []);
	var a = props.payment;

	return <Label color={a.cancelled ? 'red' : (a.paid ? 'green' : a.date_expiration<now ? 'transparent' : 'blue')} style={{ minWidth: 100, ...props.style }}>
		{a.cancelled ? t('common.cancelled') : (a.paid ? t('common.paid') : a.date_expiration<now ? t('common.expired') : t('common.pending'))}
	</Label>
}

var PaymentItem = (props: { admin?: boolean, payment?: Payment, placeholder?: boolean })=>{
	if(props.placeholder){
		return <div className="pf payment placeholder">
			<div className="left">
				<Placeholder height={10} width={60} random={false} />
				<Placeholder height={10} width={80} random={false} />
				<Placeholder height={21} width={50} variance={200} />
				<Placeholder height={11} width={80} random={false} />
			</div>
			<div className="right">
				<Placeholder height={20} width={20} variance={100} />
				<Placeholder height={22} width={90} random={false} style={{ margin: '5px 0' }} />
			</div>
		</div>
	}
	return <Link className="pf payment" to={`/pagos/${props.payment.payment_id}`}>
		<div className="left">
			<div className="meta">
				#{props.payment.payment_id}
				{props.admin ? ` • ${props.payment.client_name}` : ''}
			</div>
			<div className="meta" style={{ marginTop: 2 }}>{moment.unix(props.payment.date_created).format('DD/MM/YY HH:mm')}</div>
			<div className="name">{props.payment.card_name}</div>
			<div className="meta card">
				<Icon name={getCardBrandIcon(props.payment.card_brand)} brand />
				{('0000'+props.payment.card_digits.toString()).slice(-4)}
			</div>
		</div>
		<div className="right">
			<div className="amount">
				${addCommas(props.payment.total)}
			</div>
			<PaymentStatusLabel payment={props.payment} style={{ minWidth: 90, lineHeight: '14px', padding: '4px 10px', fontSize: 12 }} />
		</div>
	</Link>
}

const MOBILE_BREAKPOINT = 740;
var PaymentList = ()=>{
	var type = useNavigationType();
	var { t } = useTranslation();
	var { active_client, admin } = useUser();
	var [payments, setPayments] = useState<Payment[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [search, setSearch] = useState<string>(null);
	var [searching, setSearching] = useState<boolean>(false);
	var [searchingFilters, setSearchingFilters] = useState<boolean>(false);
	var [searchPayments, setSearchPayments] = useState<Payment[]>(null);
	var [mobile, setMobile] = useState<boolean>(false);
	var [filterShown, setFilterShown] = useState<boolean>(false);
	var [filters, setFilters] = useState<SearchFilter[]>();

	useEffect(()=>{
		loadPayments();
	}, []);

	useLayoutEffect(()=>{
		setMobile(window.innerWidth<=MOBILE_BREAKPOINT);
		var updateSize = ()=>{
			setMobile(window.innerWidth<=MOBILE_BREAKPOINT);
		};
		window.addEventListener('resize', updateSize);
		return ()=>{
			window.removeEventListener('resize', updateSize);
		}
	}, []);

	var loadPayments = ()=>{
		API.listPayments(active_client, 0, 30, type===NavigationType.Pop).then(res=>{
			if(res.error) return setLoadError(res.message);
			setPayments(res.data);
		}).catch(err=>{
			setLoadError(t('common.unexpected_error', { code: 'PYLST-1' }));
		});
	}

	var sendSearch = (filters: SearchFilter[])=>{
		filters = filters?.filter(a=>!!a.value && a.value.length>0);
		if((!filters || filters?.length==0) && (!search || search.length<3)) return;

		setSearching(true);
		setSearchPayments(null);
		setSearchingFilters(!!filters);
		setFilterShown(false);


		API.searchPayments(active_client, filters ? null : search, filters).then(res=>{
			if(res.error) return setLoadError(null);
			setSearchPayments(res.data);
		}).catch(err=>{
			setLoadError(t('common.unexpected_error', { code: 'PYSCH-1' }));
		}).finally(()=>{
			setSearching(false);
		})
	}

	var addSearchFilter = (key: SearchFilterType["key"])=>{
		var flt = [...filters || []];
		if(flt.findIndex(a=>a.key===key)!=-1) return;
		var type = SEARCH_FILTERS.find(a=>a.key===key);
		flt.push({
			key: type.key,
			value: '',
			type: type.type,
		});
		setFilters(flt);
	}

	var removeSearchFilter = (filter: SearchFilterType)=>{
		return ()=>{
			var flt = [...filters || []];
			flt = flt.filter(a=>a.key!==filter.key);
			setFilters(flt);
			if(flt.length==0){
				clearFilters();
			}
		}
	}

	var onSearchFilterChange = (filter: SearchFilterType)=>{
		return (val: string)=>{
			var flt = [...filters || []];
			var ix = flt.findIndex(a=>a.key===filter.key);
			if(ix==-1) return;
			flt[ix].value = val.trim();
			setFilters(flt);
		}
	}

	var clearFilters = ()=>{
		setSearching(false);
		setSearchingFilters(false);
		setLoadError(null);
		setSearchPayments(null);
	}

	var onSearchChange = (v: string)=>{
		if(v.length===0){
			setSearching(false);
			setSearchPayments(null);
			setLoadError(null);
		}
		setSearch(v)
	}

	var SearchPlaceholders = useMemo(()=>{
		var random_map = new Array(searching ? 27 : 30).fill(0);
		if(!mobile){
			var RandomPlaceholders = random_map.map((a,i)=>(
				<Table.Row key={`SRCH-PLH-${i}`} collapsingIndexes={admin ? [0, 1, 2, 4, 5, 6] : [0, 1, 3, 4, 5]} data={[
					<Placeholder width={60} random={false} />,
					<Placeholder width={80} random={false} />,
					...(admin ? [
						<Placeholder width={80} random={false} />,
					] : []),
					<Placeholder width={50} variance={200} />,
					<Placeholder width={60} random={false} />,
					<Placeholder width={80} random={false} />,
					<Placeholder width={60} random={false} />,
				]} />
			));
			return <>
				{searching && (
					<Table.Row>
						<Table.Cell colSpan={100}>
							<Header style={{ height: 40*3, margin: 0, padding: 0, marginTop: 5 }} text={'Buscando'} subtext={search ? `Buscando "${search}"` : null} subheaderStyle={{ fontSize: 14 }} loading />
						</Table.Cell>
					</Table.Row>
				)}
				{RandomPlaceholders}
			</>
		}else{
			var RandomPlaceholders = random_map.map((a, i)=>(
				<PaymentItem key={`SRCHM-PLH-${i}`} admin={admin} placeholder />
			));

			return <>
				{RandomPlaceholders}
			</>
		}
	}, [mobile, admin, searching]);

	var shown_payments = searchPayments || payments;
	
	return <div className="pf payment-list-screen">
		{!searchingFilters ? (
			<Input placeholder='Buscar por ID o correo electrónico' value={search} onChange={onSearchChange} readonly={!!searching} onSubmit={()=>sendSearch(null)} button={<>
				<Button icon iconName='filter' onClick={bindClose(setFilterShown, true)} />
				<Button text='Buscar' onClick={()=>sendSearch(null)} />
			</>} />
		) : (
			<div className="pf filters">
				<div className="filters" onClick={bindClose(setFilterShown, true)}>
					{filters.map(a=>{
						if(!a.value || a.value.length==0) return null;
						var type = SEARCH_FILTERS.find(b=>b.key===a.key);
						return<div className="item" key={`SRCHFLT-${a.key}`}>
							<div className="key">{type.short_name || type.name}</div>
							<div className="value">{a.value}</div>
						</div>
					})}
				</div>
				<Button icon iconName='filter' onClick={bindClose(setFilterShown, true)} />
				<Button icon iconName='times' color='blue' onClick={clearFilters} />
			</div>
		)}
		{loadError ? (
			<Header text={'Error'} subtext={loadError} style={{ marginTop: 30 }} />
		) : !mobile ? (
			<Table
				striped
				headers={(
					admin ? (
						['ID', 'Fecha', 'Cliente', 'Nombre', 'Método', 'Estatus', 'Monto']
					) : (
						['ID', 'Fecha', 'Nombre', 'Método', 'Estatus', 'Monto']
					)
				)}
				// collapsingIndexes={[0, 1, 3, 4, 5]}
			>
				{(payments===null && searchPayments===null) ? (
					SearchPlaceholders
				) : (
					searching && !searchPayments ? (
						SearchPlaceholders
					) : shown_payments.length>0 ? (
						shown_payments.map(a=>(
							<Table.Row
								selectable
								compact
								key={`PYM-${a.payment_id}`}
								as={Link}
								to={`/pagos/${a.payment_id}`}
								collapsingIndexes={admin ? [0, 1, 2, 4, 5, 6] : [0, 1, 3, 4, 5]}
								data={[
									a.payment_id,
									moment.unix(a.date_created).format('DD/MM/YY HH:mm'),
									...(admin ? [
										a.client_name,
									] : []),
									a.card_name,
									<>
										<Icon name={getCardBrandIcon(a.card_brand)} brand />
										{('0000'+a.card_digits.toString()).slice(-4)}
									</>,
									<PaymentStatusLabel payment={a} />,
									<div style={{ minWidth: 70 }}>
										${addCommas(a.total)}
									</div>
								]}
							/>
						))
					) : (
						<Table.Cell empty row colSpan={100}>
							{t('payment_list.empty')}
						</Table.Cell>
					)
				)}
			</Table>
		) : (
			(payments===null && searchPayments===null) ? (
				SearchPlaceholders
			) : shown_payments.length>0 ? (
				shown_payments.map(a=>(
					<PaymentItem admin={admin} key={`PYMMB-${a.payment_id}`} payment={a} />
				))
			) : null
		)}
		<Modal open={filterShown} onClose={bindClose(setFilterShown, false)} size='tiny'>
			<Modal.Header text='Busqueda avanzada' />
			<Modal.Content>
				<Dropdown placeholder='Selecciona el filtro' options={SEARCH_FILTERS.filter(a=>!filters || filters.findIndex(b=>b.key==a.key)==-1).map(a=>({
					text: a.name,
					value: a.key,
					onClick: addSearchFilter
				}))} />
				<Table details divided title='Filtros seleccionados' titleSize='small' titleCentered style={{ marginTop: 15 }}>
					{(!!filters && filters.length>0) ? (
						filters.map(a=>{
							var type = SEARCH_FILTERS.find(b=>b.key===a.key);
							if(!type) return null;
							return <Table.Row key={`SCHFL-${a.key}`}>
								<Table.Cell>{type.name}</Table.Cell>
								<Table.Cell>
									<Input style={{ margin: 0 }} inputStyle={{ padding: 8 }} value={a.value} onChange={onSearchFilterChange(type)} placeholder={type.name} button={(
										<Button iconName='trash' icon onClick={removeSearchFilter(type)} />
									)} />
								</Table.Cell>
							</Table.Row>
						})
					) : (
						<Table.Cell row>
							<Header text={'Sin filtros'} subtext={'Selecciona un filtro en la opción superior para comenzar'} />
						</Table.Cell>
					)}
				</Table>
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={bindClose(setFilterShown, false)} />
				<Button text='Buscar' color='blue' onClick={()=>sendSearch(filters)} disabled={!filters || filters.filter(a=>a.value.length>0).length==0} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default PaymentList;